'use client';

import { Button } from '@pickleballinc/react-ui';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { useCallback, useState } from 'react';

import { DashboardMyGearPickleballCentralPromoCard } from '@/modules/players/v3/components/player-pages/dashboard/my-gear/dashboard-my-gear-pickleball-central-promo-card';
import { DeleteGearDialog } from '@/modules/players/v3/components/player-pages/gear/delete-gear-dialog';
import { GearCard } from '@/modules/players/v3/components/shared/gear-card/gear-card';
import { GEAR_GRID_VISIBLE_CELL_LIMIT } from '@/modules/players/v3/constants/gear';
import { useDeletePlayerGear } from '@/modules/players/v3/hooks/use-delete-player-gear';
import { useFavoritesFirstOrder } from '@/modules/players/v3/hooks/use-favorites-first-order';
import { useGearFavorites } from '@/modules/players/v3/hooks/use-gear-favorites';
import { useGetPlayerGear } from '@/modules/players/v3/hooks/use-get-player-gear';
import { GearCategory, PlayerGear } from '@/modules/players/v3/types';
import { PROFILE_QUERY_KEY } from '@/modules/players/v3/utils/my-gear';
import { User } from '@/types/players.types';
import { fetchRoute } from '@/utils/helpers/fetchRoute';

interface MyGearGridProps {
  editPlayer: User;
  playerGear: PlayerGear[];
  showOwnerActions?: boolean;
  showAddGearCard?: boolean;
}

export const MyGearGrid = ({
  editPlayer,
  playerGear: initialPlayerGear,
  showOwnerActions = true,
  showAddGearCard,
}: MyGearGridProps) => {
  const shouldShowAddGearCard = showAddGearCard ?? showOwnerActions;
  const { slug } = useParams<{ slug: string }>();
  const [expanded, setExpanded] = useState(false);
  const [showDeleteGearModal, setShowDeleteGearModal] = useState(false);
  const [selectedPlayerGearId, setSelectedPlayerGearId] = useState<string | null>(null);
  const [favoritePendingId, setFavoritePendingId] = useState<string | null>(null);

  const { addToFavorites, removeFromFavorites } = useGearFavorites(
    editPlayer.uuid,
    PROFILE_QUERY_KEY
  );

  const { data: allPlayerGear } = useGetPlayerGear(
    editPlayer.uuid,
    initialPlayerGear,
    PROFILE_QUERY_KEY
  );

  const { data: gearCategories } = useQuery({
    queryKey: ['gear-categories'],
    queryFn: async ({ signal }) => {
      const data = await fetchRoute<{ categories: GearCategory[] }>(
        '/api/v2/gear/getGearCategories',
        {},
        signal
      );
      return data?.categories ?? [];
    },
  });

  const playerGear = useFavoritesFirstOrder(allPlayerGear, gearCategories);
  const { mutate: deletePlayerGearMutation, isPending: isDeletingPlayerGear } = useDeletePlayerGear(
    editPlayer.uuid
  );

  const handleDeletePlayerGear = useCallback(() => {
    if (!selectedPlayerGearId) {
      return;
    }

    deletePlayerGearMutation(selectedPlayerGearId, {
      onSuccess: () => {
        setShowDeleteGearModal(false);
        setSelectedPlayerGearId(null);
      },
      onError: () => {
        setShowDeleteGearModal(false);
        setSelectedPlayerGearId(null);
      },
    });
  }, [deletePlayerGearMutation, selectedPlayerGearId]);

  const handleAddToFavorites = useCallback(
    (playerGearId: string) => {
      setFavoritePendingId(playerGearId);
      addToFavorites.mutate(playerGearId, {
        onSettled: () => setFavoritePendingId(null),
      });
    },
    [addToFavorites]
  );

  const handleRemoveFromFavorites = useCallback(
    (playerGearId: string) => {
      setFavoritePendingId(playerGearId);
      removeFromFavorites.mutate(playerGearId, {
        onSettled: () => setFavoritePendingId(null),
      });
    },
    [removeFromFavorites]
  );

  const handleDeleteGear = useCallback((playerGearId: string) => {
    setSelectedPlayerGearId(playerGearId);
    setShowDeleteGearModal(true);
  }, []);

  const addGearHref = `/players/${slug}/v3/profile/add-gear`;

  const visibleGearLimit = shouldShowAddGearCard
    ? GEAR_GRID_VISIBLE_CELL_LIMIT - 1
    : GEAR_GRID_VISIBLE_CELL_LIMIT;
  const hasMore = playerGear.length > visibleGearLimit;
  const visibleGear = expanded ? playerGear : playerGear.slice(0, visibleGearLimit);

  return (
    <div className='flex flex-col gap-4'>
      <div className='grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4'>
        {shouldShowAddGearCard ? (
          <DashboardMyGearPickleballCentralPromoCard addGearHref={addGearHref} fluid />
        ) : null}
        {visibleGear.map((gear) => (
          <GearCard
            key={gear.uuid}
            gear={gear}
            fluid
            disabled={isDeletingPlayerGear}
            isFavoritePending={favoritePendingId === gear.uuid}
            showActions={showOwnerActions}
            onAddToFavorites={handleAddToFavorites}
            onRemoveFromFavorites={handleRemoveFromFavorites}
            onDeleteGear={handleDeleteGear}
          />
        ))}
      </div>

      {hasMore ? (
        <div className='flex justify-center pt-2'>
          <Button
            variant='primary'
            size='sm'
            className='w-[140px] justify-center'
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? 'See less' : 'See more'}
          </Button>
        </div>
      ) : null}

      {selectedPlayerGearId ? (
        <DeleteGearDialog
          open={showDeleteGearModal}
          onOpenChange={(open) => {
            setShowDeleteGearModal(open);
            if (!open) setSelectedPlayerGearId(null);
          }}
          onConfirm={handleDeletePlayerGear}
          onCancel={() => {
            setShowDeleteGearModal(false);
            setSelectedPlayerGearId(null);
          }}
          isDeleting={isDeletingPlayerGear}
        />
      ) : null}
    </div>
  );
};
